import { MouseEvent } from 'react';
import { FileText, Globe, File, Image, Code, MoreVertical, Pin, Trash2, Copy } from 'lucide-react';
import { cn } from '@/lib/utils.js';
import { Artifact, ArtifactType } from '@/stores/artifactPanelStore.js';
import { Button } from '@/components/ui/button.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';

interface ArtifactItemProps {
  artifact: Artifact;
  isActive: boolean;
  isOpen: boolean;
  onClick: () => void;
  onPin: () => void;
  onDelete: () => void;
  className?: string;
}

const iconMap: Record<ArtifactType, React.ComponentType<{ className?: string }>> = {
  note: FileText,
  'notes-list': FileText,
  file: File,
  image: Image,
  code: Code,
  browser: Globe,
};

export function ArtifactItem({
  artifact,
  isActive,
  isOpen,
  onClick,
  onPin,
  onDelete,
  className,
}: ArtifactItemProps) {
  const Icon = iconMap[artifact.type];

  const handleContextMenu = (e: MouseEvent) => {
    e.preventDefault();
    // Context menu will be handled by the dropdown
  };

  const handleMoreClick = (e: MouseEvent) => {
    e.stopPropagation();
  };

  const handlePinClick = (e: MouseEvent) => {
    e.stopPropagation();
    onPin();
  };

  const handleDeleteClick = (e: MouseEvent) => {
    e.stopPropagation();
    onDelete();
  };

  return (
    <div
      className={cn(
        'group flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors',
        isActive && 'bg-primary/10 text-primary',
        !isActive && 'hover:bg-muted/50 text-foreground',
        className
      )}
      onClick={onClick}
      onContextMenu={handleContextMenu}
    >
      {/* Icon */}
      <Icon className={cn('h-4 w-4 flex-shrink-0', isActive ? 'text-primary' : 'text-muted-foreground')} />

      {/* Title */}
      <span className="flex-1 text-sm truncate font-medium">
        {artifact.title}
      </span>

      {/* Indicators */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {/* Unsaved indicator */}
        {artifact.metadata?.unsaved && (
          <div className="h-1.5 w-1.5 rounded-full bg-orange-500" />
        )}

        {/* Pin indicator */}
        {artifact.isPinned && (
          <Pin className="h-3 w-3 text-muted-foreground" />
        )}

        {/* Open indicator */}
        {isOpen && !isActive && (
          <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50" />
        )}

        {/* More options */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={handleMoreClick}
            >
              <MoreVertical className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handlePinClick}>
              <Pin className="h-4 w-4 mr-2" />
              {artifact.isPinned ? 'Unpin' : 'Pin'}
            </DropdownMenuItem>
            <DropdownMenuItem>
              <Copy className="h-4 w-4 mr-2" />
              Duplicate
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleDeleteClick} className="text-destructive">
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
